import { useEffect, useRef, useState } from 'react';
import { buildQuipQueue, quipDwellMs, type QuipContext } from '@/lib/aiQuips';
import { cn } from '@/lib/utils';

interface AIThinkingLabelProps {
  context?: QuipContext;
  className?: string;
  /** Wait this many ms before showing anything. Useful on fast surfaces (e.g. search). */
  minDelayMs?: number;
  /** What the AI is doing (e.g. "Preparing brief on today’s priority…"); quips become the sub-line. */
  title?: string;
  /** Show the dancing dots. Defaults to true. */
  showDots?: boolean;
  /** 'lg' = centre-stage state for an otherwise empty surface. */
  size?: 'default' | 'lg';
}

// Survives across mounts so the same quip rarely shows back-to-back
// when the user fires consecutive AI requests.
let lastShownQuip: string | undefined;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AIThinkingLabel({
  context = 'generic',
  className,
  minDelayMs = 0,
  title,
  showDots = true,
  size = 'default',
}: AIThinkingLabelProps) {
  const reduced = prefersReducedMotion();
  const queueRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const [quip, setQuip] = useState<string | null>(
    minDelayMs > 0 ? null : initialQuip(context),
  );
  const [fading, setFading] = useState(false);

  function initialQuip(ctx: QuipContext): string {
    queueRef.current = buildQuipQueue(ctx, lastShownQuip);
    indexRef.current = 0;
    const first = queueRef.current[0];
    lastShownQuip = first;
    return first;
  }

  // Honor minDelayMs.
  useEffect(() => {
    if (quip !== null) return;
    const t = setTimeout(() => {
      setQuip(initialQuip(context));
    }, minDelayMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cycle.
  useEffect(() => {
    if (!quip) return;
    const dwell = reduced ? 3500 : quipDwellMs(quip);
    const fadeMs = reduced ? 0 : 200;

    const fadeTimer = setTimeout(() => {
      if (!reduced) setFading(true);
    }, dwell - fadeMs);

    const swapTimer = setTimeout(() => {
      indexRef.current += 1;
      if (indexRef.current >= queueRef.current.length) {
        queueRef.current = buildQuipQueue(context, lastShownQuip);
        indexRef.current = 0;
      }
      const next = queueRef.current[indexRef.current];
      lastShownQuip = next;
      setQuip(next);
      setFading(false);
    }, dwell);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(swapTimer);
    };
  }, [quip, context, reduced]);

  if (!quip) return null;

  const quipText = (
    <span className={cn('transition-opacity duration-200', fading ? 'opacity-0' : 'opacity-100')}>{quip}</span>
  );

  // With a task title, the title stays put and the quips become the smaller second line.
  if (title && size === 'lg') {
    return (
      <span role="status" aria-live="polite" className={cn('flex flex-col items-center gap-4 text-center', className)}>
        <AIDots className="scale-150" />
        <span className="font-display text-xl sm:text-2xl text-foreground">{title}</span>
        <span className="text-base text-muted-foreground min-h-[1.5rem]">{quipText}</span>
      </span>
    );
  }

  if (title) {
    return (
      <span role="status" aria-live="polite" className={cn('inline-flex items-start gap-2.5', className)}>
        <AIDots className="mt-[7px]" />
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">{quipText}</span>
        </span>
      </span>
    );
  }

  return (
    <span aria-live="polite" aria-atomic="false" className={cn('inline-flex items-center gap-2', className)}>
      {showDots && <AIDots />}
      {quipText}
    </span>
  );
}

/** Three dancing dots — the shared "AI is working" signal. */
export function AIDots({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('ai-dots', className)}>
      <span /><span /><span />
    </span>
  );
}
